export interface ApiErrorDetail {
  field: string;
  code: string;
  message: string;
}

/** Body returned by the API for every non-2xx response. */
export interface ApiErrorBody {
  statusCode: number;
  code: string;
  message: string;
  params?: Record<string, unknown>;
  details?: ApiErrorDetail[];
  correlationId?: string;
  timestamp: string;
  path?: string;
}

/**
 * Thrown for every failed request.
 *
 * `code` is the stable contract; `message` is an English developer fallback.
 * Clients should render `translationKey` through their i18n layer so users see
 * the message in their own language.
 */
export class ApiError extends Error {
  readonly statusCode: number;
  readonly code: string;
  readonly params: Record<string, unknown>;
  readonly details: ApiErrorDetail[];
  readonly correlationId?: string;
  readonly path?: string;

  constructor(body: ApiErrorBody) {
    super(body.message);
    this.name = 'ApiError';
    this.statusCode = body.statusCode;
    this.code = body.code;
    this.params = body.params ?? {};
    this.details = body.details ?? [];
    this.correlationId = body.correlationId;
    this.path = body.path;
  }

  get translationKey(): string {
    return `errors.${this.code}`;
  }

  is(code: string): boolean {
    return this.code === code;
  }

  get isValidation(): boolean {
    return this.statusCode === 400 || this.statusCode === 422;
  }

  get isAuth(): boolean {
    return this.statusCode === 401;
  }

  get isForbidden(): boolean {
    return this.statusCode === 403;
  }

  /** Field → message map, ready to feed into a form. */
  fieldErrors(): Record<string, string> {
    return Object.fromEntries(this.details.map((detail) => [detail.field, detail.message]));
  }
}

/** Raised when the network fails before the server could answer. */
export class NetworkError extends Error {
  constructor(
    message: string,
    override readonly cause?: unknown,
  ) {
    super(message);
    this.name = 'NetworkError';
  }
}
